/**
 * «¿Lo leímos bien?» (DISENO §3, PRODUCTO §7): por cada dato, la columna elegida, tres valores del archivo y cómo los
 * leemos, con el desplegable para cambiar la columna; después, lo que se va a guardar, lo que frena el guardado y los
 * avisos. Lo que la persona cambia lo manda la página al backend, que devuelve la revisión nueva.
 */

import { AlertTriangle, Check, CircleSlash, Info } from "lucide-react";
import type { Archivo, CampoRevisado, Carga, Nota, Revision } from "../types";
import {
  TEXTO_ESTADO, camposDe, estadoCampo, numerosResumen, opcionesDe, textoNoLeidos, valorDe, type EstadoCampo,
} from "../logic/revision";
import { num } from "../../campana-nueva/utils/format";

const ARCHIVO_TEXTO: Record<Archivo, string> = { ventas: "Ventas", clientes: "Clientes" };

/** El id del bloque de frenos: «Confirmar y guardar» lo nombra para decir por qué está apagado. */
export const ID_FRENOS = "frenos-base";

function IconoEstado({ estado }: { estado: EstadoCampo }) {
  const texto = TEXTO_ESTADO[estado];
  if (estado === "ok") return <Check size={20} strokeWidth={2.4} className="text-emerald-700" role="img" aria-label={texto} />;
  if (estado === "sin_columna") return <CircleSlash size={18} className="text-slate-400" role="img" aria-label={texto} />;
  return <AlertTriangle size={18} className={estado === "falta" ? "text-red-600" : "text-amber-600"} role="img" aria-label={texto} />;
}

function FilaCampo({ c, deshabilitada, onColumna }: { c: CampoRevisado; deshabilitada: boolean; onColumna: (c: CampoRevisado, valor: string) => void }) {
  const estado = estadoCampo(c);
  const sinLeer = textoNoLeidos(c);
  return (
    <tr className="border-t border-slate-100 align-top">
      <th scope="row" className="py-3 px-4 text-left text-sm font-semibold text-slate-900 whitespace-nowrap">
        {c.etiqueta}
        {!c.obligatorio && <span className="block text-xs font-normal text-slate-400">opcional</span>}
      </th>
      <td className="py-2 px-4">
        <select value={valorDe(c)} disabled={deshabilitada} onChange={(e) => onColumna(c, e.target.value)}
          aria-label={`Columna para ${c.etiqueta} (${ARCHIVO_TEXTO[c.archivo].toLowerCase()})`}
          className={`min-h-[44px] max-w-[220px] rounded-lg border bg-white px-2 text-sm disabled:opacity-60 ${estado === "falta" ? "border-red-300" : "border-slate-200"}`}>
          {opcionesDe(c).map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
        </select>
      </td>
      <td className="py-3 px-4 font-mono text-xs text-slate-500">
        {c.ejemplos.length ? c.ejemplos.map((x, i) => <span key={i} className="block truncate max-w-[200px] leading-6" title={x.crudo}>{x.crudo || "—"}</span>) : "—"}
      </td>
      <td className="py-3 px-4 font-mono text-xs font-medium text-slate-900">
        {c.ejemplos.length
          ? c.ejemplos.map((x, i) => (
            <span key={i} className="block truncate max-w-[200px] leading-6" title={x.leido}>
              {x.leido || <span className="font-sans font-normal text-amber-700">no se pudo leer</span>}
            </span>
          ))
          : "—"}
      </td>
      <td className="py-3 px-4">
        <IconoEstado estado={estado} />
        {sinLeer && <span className="block mt-1 text-xs text-amber-700 whitespace-nowrap">{sinLeer}</span>}
      </td>
    </tr>
  );
}

function TablaArchivo(p: { archivo: Archivo; nombre: string; filas: number | null; campos: CampoRevisado[]; deshabilitada: boolean; onColumna: (c: CampoRevisado, valor: string) => void }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm" data-testid={`revision-${p.archivo}`}>
        <caption className="text-left px-4 pt-4 pb-2 text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{ARCHIVO_TEXTO[p.archivo]}</span>
          {p.nombre && <> · <span className="font-mono">{p.nombre}</span></>}
          {p.filas !== null && <> · {num(p.filas)} filas</>}
        </caption>
        <thead>
          <tr className="text-left text-xs text-slate-500">
            <th scope="col" className="py-2 px-4 font-medium">Dato</th>
            <th scope="col" className="py-2 px-4 font-medium">Tu columna</th>
            <th scope="col" className="py-2 px-4 font-medium">En tu archivo</th>
            <th scope="col" className="py-2 px-4 font-medium">Lo leemos así</th>
            <th scope="col" className="py-2 px-4 font-medium"><span className="sr-only">Estado</span></th>
          </tr>
        </thead>
        <tbody>
          {p.campos.map((c) => <FilaCampo key={c.campo} c={c} deshabilitada={p.deshabilitada} onColumna={p.onColumna} />)}
        </tbody>
      </table>
    </div>
  );
}

function AvisoNota({ n }: { n: Nota }) {
  // el backend pone título sólo a lo que hay que atender antes de mandar (el consentimiento)
  const fuerte = !!n.titulo;
  return (
    <div role="note" className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm ${fuerte ? "bg-amber-50 text-amber-900" : "bg-slate-100 text-slate-700"}`}>
      {fuerte ? <AlertTriangle size={18} className="flex-shrink-0 text-amber-600" aria-hidden="true" /> : <Info size={18} className="flex-shrink-0 text-slate-500" aria-hidden="true" />}
      <span>{n.titulo && <strong>{n.titulo} </strong>}{n.texto}</span>
    </div>
  );
}

export function RevisionCarga({ revision, lectura, deshabilitada, onColumna }: {
  revision: Revision;
  lectura: Carga["lectura"];
  deshabilitada: boolean;
  onColumna: (c: CampoRevisado, valor: string) => void;
}) {
  return (
    <div className="space-y-5">
      {(["ventas", "clientes"] as const).map((a) => (
        <TablaArchivo key={a} archivo={a} nombre={lectura?.[a]?.nombre || ""} filas={lectura?.[a]?.filas ?? null}
          campos={camposDe(revision.campos, a)} deshabilitada={deshabilitada} onColumna={onColumna} />
      ))}

      <section aria-labelledby="resumen-base" className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
        <h2 id="resumen-base" className="text-lg font-semibold text-slate-900">Lo que vamos a guardar</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="resumen-base">
          {numerosResumen(revision.resumen).map((n) => (
            <div key={n.etiqueta} className="rounded-xl bg-slate-50 px-4 py-3">
              <p className="text-sm text-slate-500">{n.etiqueta}</p>
              <p className="text-xl font-bold text-slate-900 tabular-nums">{n.valor}</p>
              {n.detalle && <p className="text-xs text-slate-500">{n.detalle}</p>}
            </div>
          ))}
        </div>
      </section>

      {revision.frenos.length > 0 && (
        <section id={ID_FRENOS} aria-labelledby="frenos-titulo" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
          <h2 id="frenos-titulo" className="font-semibold">Para guardar, falta resolver esto:</h2>
          <ul className="mt-1 list-disc pl-5 space-y-1">
            {revision.frenos.map((f) => <li key={`${f.codigo}:${f.texto}`}>{f.titulo && <strong>{f.titulo} </strong>}{f.texto}</li>)}
          </ul>
        </section>
      )}

      {revision.avisos.length > 0 && (
        <div className="space-y-2.5">
          {revision.avisos.map((n) => <AvisoNota key={`${n.codigo}:${n.texto}`} n={n} />)}
        </div>
      )}
    </div>
  );
}
